import { useState, useEffect, useMemo } from 'react';
import { cn, formatDate, timeAgo } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Drawer } from '@/components/ui/Drawer';
import { Tabs } from '@/components/ui/Tabs';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import type { User, Role, Permission } from '@/types';
import {
  Users,
  Shield,
  UserCheck,
  Key,
  Plus,
  Search,
  Filter,
  X,
  Mail,
  Lock,
  CheckCircle2,
  XCircle,
  Copy,
  Check,
  Trash2,
  RefreshCw,
  Eye,
  Edit2,
  ChevronRight,
  ShieldAlert,
  Clock,
  Laptop,
  Terminal,
  Server,
  Sliders,
  LogOut,
  UserPlus,
  Briefcase,
} from 'lucide-react';

const ALL_PERMISSIONS: { id: Permission; label: string; category: string }[] = [
  { id: 'view_devices', label: 'View Devices', category: 'Device Management' },
  { id: 'manage_devices', label: 'Manage Devices', category: 'Device Management' },
  { id: 'control_devices', label: 'Control Devices', category: 'Device Management' },
  { id: 'remote_desktop', label: 'Remote Desktop', category: 'Remote Access' },
  { id: 'open_terminal', label: 'Terminal Console', category: 'Remote Access' },
  { id: 'transfer_files', label: 'File Explorer Transfer', category: 'Remote Access' },
  { id: 'run_scripts', label: 'Execute Scripts', category: 'Automation & Scripts' },
  { id: 'manage_scripts', label: 'Author & Manage Scripts', category: 'Automation & Scripts' },
  { id: 'manage_patches', label: 'Manage Patch Policies', category: 'Patch Management' },
  { id: 'manage_clients', label: 'Manage Clients & Sites', category: 'Administration' },
  { id: 'manage_users', label: 'Manage Users & Roles', category: 'Administration' },
  { id: 'manage_alerts', label: 'Manage Alert Rules', category: 'Administration' },
  { id: 'manage_settings', label: 'Manage System Settings', category: 'Administration' },
  { id: 'view_reports', label: 'View Executive Reports', category: 'Audit & Compliance' },
  { id: 'view_audit_log', label: 'View Compliance Audit Log', category: 'Audit & Compliance' },
];

interface TeamItem {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  members: string[]; // user emails/names
  createdAt: string;
}

interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string | null;
  status: 'active' | 'revoked';
}

export function UsersRoles() {
  const [activeTab, setActiveTab] = useState('users');

  // Main Domain Data
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  // Teams State
  const [teams, setTeams] = useState<TeamItem[]>([
    {
      id: 'team-1',
      name: 'L1 Service Desk',
      description: 'Frontline helpdesk technicians handling user support & basic remediation',
      memberCount: 3,
      members: ['jsmith@insightnode.io', 'sarah.b@insightnode.io'],
      createdAt: '2026-01-15T08:00:00Z',
    },
    {
      id: 'team-2',
      name: 'Infrastructure & Cloud',
      description: 'Server administrators, domain controllers, and cloud cluster ops',
      memberCount: 2,
      members: ['admin@insightnode.io', 'mlee@insightnode.io'],
      createdAt: '2026-01-10T10:30:00Z',
    },
    {
      id: 'team-3',
      name: 'Security Operations',
      description: 'SOC team inspecting audit trails, threat detection, and patch compliance',
      memberCount: 2,
      members: ['david.w@insightnode.io', 'admin@insightnode.io'],
      createdAt: '2026-02-01T14:15:00Z',
    },
  ]);

  // API Keys State
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([
    {
      id: 'key-1',
      name: 'CI/CD Deployment Pipeline',
      keyPrefix: 'ink_live_8f3a...',
      permissions: ['run_scripts', 'manage_patches'],
      createdAt: '2026-03-01T12:00:00Z',
      lastUsed: '2026-08-19T22:15:00Z',
      status: 'active',
    },
    {
      id: 'key-2',
      name: 'SIEM Integration Collector',
      keyPrefix: 'ink_live_92bc...',
      permissions: ['view_audit_log', 'view_reports'],
      createdAt: '2026-04-12T09:30:00Z',
      lastUsed: '2026-08-20T00:10:00Z',
      status: 'active',
    },
    {
      id: 'key-3',
      name: 'Legacy Monitoring Webhook',
      keyPrefix: 'ink_live_11aa...',
      permissions: ['view_devices'],
      createdAt: '2026-02-10T16:00:00Z',
      lastUsed: '2026-05-10T11:20:00Z',
      status: 'revoked',
    },
  ]);

  // Filters for Users
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<string>('all');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');

  // Modals & Drawers
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    role: 'Technician',
    teams: [] as string[],
  });

  const [createRoleModalOpen, setCreateRoleModalOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    permissions: ['view_devices', 'control_devices'] as Permission[],
  });

  const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });

  const [generateKeyModalOpen, setGenerateKeyModalOpen] = useState(false);
  const [newKey, setNewKey] = useState({ name: '', permissions: ['view_devices'] });
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Selected User Drawer
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userActiveSessions, setUserActiveSessions] = useState([
    { id: 'sess-1', device: 'Chrome / Windows 11', ip: '192.168.1.105', location: 'New York, US', startedAt: '2026-08-19T20:00:00Z' },
    { id: 'sess-2', device: 'InsightNode CLI', ip: '10.0.4.12', location: 'London, UK', startedAt: '2026-08-19T14:20:00Z' },
  ]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [u, r] = await Promise.all([mockApi.getUsers(), mockApi.getRoles()]);
        setUsers(u);
        setRoles(r);
      } catch (err) {
        toast('Failed to load users & roles', 'error');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Filtered Users list
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        (u.fullName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(userSearch.toLowerCase());

      const matchesStatus = userStatusFilter === 'all' || u.status === userStatusFilter;
      const matchesRole = userRoleFilter === 'all' || (u.role || '').toLowerCase() === userRoleFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [users, userSearch, userStatusFilter, userRoleFilter]);

  // Handlers
  const handleInviteUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.email || !newUser.fullName) {
      toast('Full name and email are required', 'error');
      return;
    }

    const created: User = {
      id: `user-${Date.now()}`,
      tenantId: 'tenant-1',
      email: newUser.email,
      fullName: newUser.fullName,
      role: newUser.role,
      status: 'invited',
      lastLogin: null,
      mfaEnabled: false,
      teams: newUser.teams.length > 0 ? newUser.teams : ['L1 Service Desk'],
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${newUser.fullName}`,
      createdAt: new Date().toISOString(),
    };

    setUsers([created, ...users]);
    setInviteUserModalOpen(false);
    setNewUser({ fullName: '', email: '', role: 'Technician', teams: [] });
    toast(`Invitation sent to ${created.email}`);
  };

  const handleCreateRole = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRole.name) {
      toast('Role name is required', 'error');
      return;
    }

    const created: Role = {
      id: `role-${Date.now()}`,
      name: newRole.name,
      description: newRole.description || 'Custom defined role',
      isSystem: false,
      permissions: newRole.permissions,
      userCount: 0,
      color: newRole.color,
    };

    setRoles([...roles, created]);
    setCreateRoleModalOpen(false);
    setNewRole({ name: '', description: '', color: '#3b82f6', permissions: ['view_devices'] });
    toast(`Role "${created.name}" created successfully`);
  };

  const handleCreateTeam = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeam.name) {
      toast('Team name is required', 'error');
      return;
    }

    const created: TeamItem = {
      id: `team-${Date.now()}`,
      name: newTeam.name,
      description: newTeam.description,
      memberCount: 0,
      members: [],
      createdAt: new Date().toISOString(),
    };

    setTeams([...teams, created]);
    setCreateTeamModalOpen(false);
    setNewTeam({ name: '', description: '' });
    toast(`Team "${created.name}" created`);
  };

  const handleGenerateKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.name) {
      toast('API key name is required', 'error');
      return;
    }

    const secret = `ink_live_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    const created: ApiKeyItem = {
      id: `key-${Date.now()}`,
      name: newKey.name,
      keyPrefix: `${secret.slice(0, 12)}...`,
      permissions: newKey.permissions,
      createdAt: new Date().toISOString(),
      lastUsed: null,
      status: 'active',
    };

    setApiKeys([created, ...apiKeys]);
    setGeneratedSecret(secret);
    toast('API key generated');
  };

  const handleRevokeKey = (id: string) => {
    setApiKeys(apiKeys.map((k) => (k.id === id ? { ...k, status: 'revoked' } : k)));
    toast('API Key revoked');
  };

  const handleRevokeSession = (sessionId: string) => {
    setUserActiveSessions(userActiveSessions.filter((s) => s.id !== sessionId));
    toast('Session revoked');
  };

  const togglePermissionCheckbox = (perm: Permission) => {
    if (newRole.permissions.includes(perm)) {
      setNewRole({ ...newRole, permissions: newRole.permissions.filter((p) => p !== perm) });
    } else {
      setNewRole({ ...newRole, permissions: [...newRole.permissions, perm] });
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-primary tracking-tight">Users & Access Control</h1>
            <Badge variant="default" className="text-xs font-mono">
              {users.length} Users • {roles.length} Roles
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Role-based access control (RBAC), team memberships, active session management, and API tokens.
          </p>
        </div>

        {/* Tab-based primary action button */}
        {activeTab === 'users' && (
          <Button variant="primary" onClick={() => setInviteUserModalOpen(true)} className="gap-2 shrink-0">
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        )}
        {activeTab === 'roles' && (
          <Button variant="primary" onClick={() => setCreateRoleModalOpen(true)} className="gap-2 shrink-0">
            <Plus className="h-4 w-4" />
            Create Role
          </Button>
        )}
        {activeTab === 'teams' && (
          <Button variant="primary" onClick={() => setCreateTeamModalOpen(true)} className="gap-2 shrink-0">
            <Plus className="h-4 w-4" />
            Create Team
          </Button>
        )}
        {activeTab === 'apikeys' && (
          <Button variant="primary" onClick={() => { setGeneratedSecret(null); setGenerateKeyModalOpen(true); }} className="gap-2 shrink-0">
            <Key className="h-4 w-4" />
            Generate Key
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'users', label: 'Users', count: users.length },
          { id: 'roles', label: 'Roles & Permissions', count: roles.length },
          { id: 'teams', label: 'Teams', count: teams.length },
          { id: 'apikeys', label: 'API Keys', count: apiKeys.filter((k) => k.status === 'active').length },
        ]}
        defaultActive="users"
        onChange={(id) => setActiveTab(id)}
      />

      {/* ==================== TAB 1: USERS ==================== */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* User Filter Bar */}
          <div className="surface p-3 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-1 flex-wrap items-center gap-3 w-full">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search user by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="input-base pl-9 text-xs"
                />
                {userSearch && (
                  <button onClick={() => setUserSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <select
                value={userStatusFilter}
                onChange={(e) => setUserStatusFilter(e.target.value)}
                className="input-base text-xs py-1.5 w-36"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="invited">Invited</option>
              </select>

              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
                className="input-base text-xs py-1.5 w-40"
              >
                <option value="all">All Roles</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {(userSearch || userStatusFilter !== 'all' || userRoleFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUserSearch('');
                  setUserStatusFilter('all');
                  setUserRoleFilter('all');
                }}
                className="text-xs text-text-muted"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Users Table */}
          {loading ? (
            <LoadingState rows={6} />
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              icon={<Users className="h-10 w-10 stroke-[1.5]" />}
              title="No users match search criteria"
              description="Try adjusting filters or invite a new team member."
            />
          ) : (
            <div className="surface overflow-hidden rounded-lg">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-base-elevated border-b border-border text-text-muted font-medium uppercase tracking-wider">
                    <tr>
                      <th className="py-3 px-4">User</th>
                      <th className="py-3 px-4">Role</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">MFA</th>
                      <th className="py-3 px-4">Teams</th>
                      <th className="py-3 px-4">Last Login</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers.map((user) => {
                      const roleObj = roles.find((r) => (r.name || '').toLowerCase() === (user.role || '').toLowerCase());
                      const roleColor = roleObj?.color || '#3b82f6';

                      return (
                        <tr
                          key={user.id}
                          onClick={() => setSelectedUser(user)}
                          className="hover:bg-base-hover/50 transition-colors cursor-pointer group"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-base-elevated border border-border flex items-center justify-center font-bold text-xs text-accent overflow-hidden shrink-0">
                                {user.avatar ? (
                                  <img src={user.avatar} alt={user.fullName} className="h-full w-full object-cover" />
                                ) : (
                                  (user.fullName || user.email || '?').charAt(0)
                                )}
                              </div>
                              <div>
                                <div className="font-semibold text-text-primary group-hover:text-accent transition-colors">
                                  {user.fullName}
                                </div>
                                <div className="text-2xs text-text-muted">{user.email}</div>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4">
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-2xs font-semibold border"
                              style={{
                                color: roleColor,
                                borderColor: `${roleColor}40`,
                                backgroundColor: `${roleColor}12`,
                              }}
                            >
                              <Shield className="h-3 w-3" />
                              {user.role}
                            </span>
                          </td>

                          <td className="py-3 px-4">
                            <Badge
                              variant={user.status === 'active' ? 'online' : user.status === 'disabled' ? 'critical' : 'warning'}
                              dot
                            >
                              <span className="capitalize">{user.status}</span>
                            </Badge>
                          </td>

                          <td className="py-3 px-4">
                            {user.mfaEnabled ? (
                              <span className="inline-flex items-center gap-1 text-status-online text-2xs font-medium">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Enabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-text-disabled text-2xs">
                                <XCircle className="h-3.5 w-3.5" />
                                Disabled
                              </span>
                            )}
                          </td>

                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {(user.teams ?? []).map((t, idx) => (
                                <span key={idx} className="bg-base-elevated border border-border px-1.5 py-0.5 rounded text-2xs text-text-secondary">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </td>

                          <td className="py-3 px-4 text-text-muted text-2xs font-mono">
                            {user.lastLogin ? timeAgo(user.lastLogin) : 'Never'}
                          </td>

                          <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedUser(user)}
                              className="h-7 px-2 text-xs text-text-secondary hover:text-text-primary"
                            >
                              Manage
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== TAB 2: ROLES ==================== */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => (
              <Card key={role.id} className="p-4 space-y-3 flex flex-col justify-between hover:border-border-strong transition-all">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: role.color }}
                      />
                      <h3 className="font-bold text-text-primary text-sm">{role.name}</h3>
                    </div>

                    <Badge variant={role.isSystem ? 'default' : 'info'} className="text-2xs">
                      {role.isSystem ? 'System Role' : 'Custom Role'}
                    </Badge>
                  </div>

                  <p className="text-xs text-text-muted line-clamp-2 min-h-[32px]">
                    {role.description}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-text-secondary pt-2 border-t border-border">
                    <div>
                      <span className="text-text-muted text-2xs block uppercase font-medium">Assigned Users</span>
                      <span className="font-semibold text-text-primary tabular">{role.userCount || 0}</span>
                    </div>

                    <div>
                      <span className="text-text-muted text-2xs block uppercase font-medium">Permissions</span>
                      <span className="font-semibold text-accent tabular">{(role.permissions ?? []).length} / 15</span>
                    </div>
                  </div>

                  {/* Permission Pills */}
                  <div className="space-y-1.5">
                    <span className="text-2xs text-text-muted font-medium uppercase tracking-wider block">Granted Capabilities</span>
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions ?? []).slice(0, 6).map((perm) => (
                        <span key={String(perm)} className="bg-base-elevated border border-border px-1.5 py-0.5 rounded text-2xs text-text-secondary font-mono">
                          {String(perm)}
                        </span>
                      ))}
                      {(role.permissions ?? []).length > 6 && (
                        <span className="bg-accent/10 text-accent border border-accent/20 px-1.5 py-0.5 rounded text-2xs font-semibold">
                          +{(role.permissions ?? []).length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-2xs text-text-muted font-mono">{role.id}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNewRole({
                        name: `${role.name} (Copy)`,
                        description: role.description,
                        color: role.color,
                        permissions: [...(role.permissions ?? [])],
                      });
                      setCreateRoleModalOpen(true);
                    }}
                    className="text-2xs h-7 px-2 text-text-secondary hover:text-text-primary"
                  >
                    Clone Role
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ==================== TAB 3: TEAMS ==================== */}
      {activeTab === 'teams' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((team) => (
              <Card key={team.id} className="p-4 space-y-3 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
                        <Briefcase className="h-4 w-4" />
                      </div>
                      <h3 className="font-bold text-text-primary text-sm">{team.name}</h3>
                    </div>
                    <Badge variant="default" className="font-mono text-2xs">
                      {team.members.length} Members
                    </Badge>
                  </div>

                  <p className="text-xs text-text-muted">{team.description}</p>

                  <div className="surface-elevated p-2.5 rounded text-xs space-y-1.5">
                    <span className="text-2xs text-text-muted font-medium uppercase tracking-wider block">Team Members</span>
                    <div className="space-y-1">
                      {team.members.map((m, i) => (
                        <div key={i} className="flex items-center gap-2 text-text-secondary text-2xs">
                          <UserCheck className="h-3 w-3 text-status-online" />
                          <span className="truncate">{m}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-2 text-2xs text-text-muted flex justify-between items-center">
                  <span>Created {formatDate(team.createdAt, 'MMM d, yyyy')}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ==================== TAB 4: API KEYS ==================== */}
      {activeTab === 'apikeys' && (
        <div className="space-y-4">
          <div className="surface overflow-hidden rounded-lg">
            <table className="w-full text-left text-xs">
              <thead className="bg-base-elevated border-b border-border text-text-muted font-medium uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">Key Name</th>
                  <th className="py-3 px-4">Prefix</th>
                  <th className="py-3 px-4">Permissions</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Created</th>
                  <th className="py-3 px-4">Last Used</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-base-hover/50 transition-colors">
                    <td className="py-3 px-4 font-semibold text-text-primary flex items-center gap-2">
                      <Key className="h-3.5 w-3.5 text-accent" />
                      {key.name}
                    </td>

                    <td className="py-3 px-4">
                      <code className="bg-base-elevated border border-border px-2 py-0.5 rounded text-2xs text-accent font-mono">
                        {key.keyPrefix}
                      </code>
                    </td>

                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {key.permissions.map((p, i) => (
                          <span key={i} className="bg-base-elevated border border-border px-1.5 py-0.5 rounded text-2xs text-text-secondary font-mono">
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <Badge variant={key.status === 'active' ? 'online' : 'critical'} dot>
                        <span className="capitalize">{key.status}</span>
                      </Badge>
                    </td>

                    <td className="py-3 px-4 text-text-muted">{formatDate(key.createdAt, 'MMM d, yyyy')}</td>

                    <td className="py-3 px-4 text-text-muted font-mono">{key.lastUsed ? timeAgo(key.lastUsed) : 'Never'}</td>

                    <td className="py-3 px-4 text-right">
                      {key.status === 'active' && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRevokeKey(key.id)}
                          className="h-7 text-xs"
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* 1. Invite User Modal */}
      <Modal
        open={inviteUserModalOpen}
        onClose={() => setInviteUserModalOpen(false)}
        title="Invite New User"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setInviteUserModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleInviteUser}>
              Send Invite
            </Button>
          </>
        }
      >
        <form onSubmit={handleInviteUser} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Full Name <span className="text-status-critical">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Alex Morgan"
              value={newUser.fullName}
              onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Email Address <span className="text-status-critical">*</span>
            </label>
            <input
              type="email"
              required
              placeholder="alex.morgan@company.com"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Assigned Role</label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="input-base"
            >
              {roles.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name} ({r.description})
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

      {/* 2. Create Role Modal */}
      <Modal
        open={createRoleModalOpen}
        onClose={() => setCreateRoleModalOpen(false)}
        title="Create Custom Role"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateRoleModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateRole}>
              Save Role
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateRole} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-text-secondary mb-1">Role Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Script Author, Level 2 Escalation"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                className="input-base"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Badge Color</label>
              <input
                type="color"
                value={newRole.color}
                onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                className="input-base h-9 p-1 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Description</label>
            <input
              type="text"
              placeholder="Brief description of responsibilities"
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              className="input-base"
            />
          </div>

          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-text-primary uppercase tracking-wider">
                Capabilities & Permissions ({newRole.permissions.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewRole({ ...newRole, permissions: ALL_PERMISSIONS.map((p) => p.id) })}
                  className="text-2xs text-accent hover:underline"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setNewRole({ ...newRole, permissions: [] })}
                  className="text-2xs text-text-muted hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-1">
              {ALL_PERMISSIONS.map((perm) => {
                const checked = newRole.permissions.includes(perm.id);
                return (
                  <label
                    key={perm.id}
                    className={cn(
                      'surface p-2.5 rounded flex items-center gap-2.5 cursor-pointer border transition-colors',
                      checked ? 'border-accent bg-accent/5' : 'border-border'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePermissionCheckbox(perm.id)}
                      className="rounded border-border text-accent focus:ring-accent h-4 w-4"
                    />
                    <div>
                      <div className="text-xs font-semibold text-text-primary">{perm.label}</div>
                      <div className="text-2xs text-text-muted font-mono">{perm.id}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </form>
      </Modal>

      {/* 3. Create Team Modal */}
      <Modal
        open={createTeamModalOpen}
        onClose={() => setCreateTeamModalOpen(false)}
        title="Create Team"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateTeamModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateTeam}>
              Create Team
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateTeam} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Team Name *</label>
            <input
              type="text"
              required
              placeholder="e.g. Tier-3 Escalation"
              value={newTeam.name}
              onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Description</label>
            <textarea
              placeholder="Team domain and duties..."
              value={newTeam.description}
              onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
              className="input-base h-20"
            />
          </div>
        </form>
      </Modal>

      {/* 4. Generate Key Modal */}
      <Modal
        open={generateKeyModalOpen}
        onClose={() => setGenerateKeyModalOpen(false)}
        title="Generate New API Key"
        size="md"
        footer={
          generatedSecret ? (
            <Button variant="primary" onClick={() => setGenerateKeyModalOpen(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setGenerateKeyModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleGenerateKey}>
                Generate Token
              </Button>
            </>
          )
        }
      >
        {generatedSecret ? (
          <div className="space-y-4">
            <div className="p-3 bg-status-warning/10 border border-status-warning/30 rounded-lg text-xs text-status-warning space-y-1">
              <div className="font-bold">Copy your key now!</div>
              <div>This key will never be shown again. Store it securely in your secrets manager.</div>
            </div>

            <div className="surface p-3 rounded font-mono text-xs text-accent flex items-center justify-between break-all">
              <span>{generatedSecret}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(generatedSecret);
                  setCopiedSecret(true);
                  setTimeout(() => setCopiedSecret(false), 2000);
                }}
                className="shrink-0 ml-2"
              >
                {copiedSecret ? <Check className="h-4 w-4 text-status-online" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleGenerateKey} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Key Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Prometheus Exporter, Zapier Automation"
                value={newKey.name}
                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                className="input-base"
              />
            </div>
          </form>
        )}
      </Modal>

      {/* Selected User Management Drawer */}
      <Drawer
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        title={selectedUser ? `Manage User: ${selectedUser.fullName}` : ''}
        width={520}
      >
        {selectedUser && (
          <div className="p-5 space-y-6">
            <div className="surface p-4 rounded-lg flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center font-bold text-accent text-lg">
                {selectedUser.fullName.charAt(0)}
              </div>
              <div>
                <h3 className="text-sm font-bold text-text-primary">{selectedUser.fullName}</h3>
                <p className="text-xs text-text-muted">{selectedUser.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={selectedUser.status === 'active' ? 'online' : 'critical'}>
                    {selectedUser.status}
                  </Badge>
                  <span className="text-2xs text-text-muted">Joined {formatDate(selectedUser.createdAt, 'MMM YYYY')}</span>
                </div>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
                <Laptop className="h-4 w-4 text-accent" />
                Active Sessions
              </h4>

              <div className="space-y-2">
                {userActiveSessions.map((sess) => (
                  <div key={sess.id} className="surface p-3 rounded text-xs flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-text-primary">{sess.device}</div>
                      <div className="text-2xs text-text-muted font-mono">{sess.ip} • {sess.location}</div>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRevokeSession(sess.id)}
                      className="h-6 text-2xs"
                    >
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
